import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ARCA_COLORS = {
  primary: '#6B8E23',
  secondary: '#2C5234',
  light: '#F8F9FA',
  dark: '#212529'
};

const VolunteerApplications = ({ user }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });

  useEffect(() => {
    fetchApplications();
    fetchStatistics();
  }, [filterStatus, pagination.page]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      if (!token) {
        toast.error('You must be logged in as an admin to view volunteer applications.');
        setLoading(false);
        return;
      }

      console.log('🔄 Fetching volunteer applications from:', `${API_URL}/admin/volunteers/applications`);

      const response = await axios.get(`${API_URL}/admin/volunteers/applications`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          status: filterStatus === 'all' ? undefined : filterStatus,
          search: searchTerm || undefined,
          limit: pagination.limit,
          page: pagination.page
        }
      });

      console.log('✅ Response:', response.data);

      if (response.data.success) {
        const apps = response.data.data?.applications || [];
        const pag = response.data.data?.pagination || {};
        setApplications(apps);
        setPagination(prev => ({
          ...prev,
          total: pag.totalApplications || 0,
          totalPages: pag.totalPages || 0
        }));
        console.log(`✅ Loaded ${apps.length} volunteer applications`);
      } else {
        toast.error('Failed to load applications');
      }
    } catch (error) {
      console.error('❌ Error fetching volunteer applications:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view volunteer applications.');
      } else if (error.response?.status === 404) {
        toast.error('Volunteer applications endpoint not found. Please restart the backend server.');
      } else {
        toast.error('Failed to load volunteer applications. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) return;

      const response = await axios.get(`${API_URL}/admin/volunteers/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const viewApplication = (application) => {
    setSelectedApplication(application);
    setShowModal(true);
  };

  const updateStatus = async (applicationId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `${API_URL}/admin/volunteers/applications/${applicationId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Application status updated successfully!');
      fetchApplications();
      setShowModal(false);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status. Please try again.');
    }
  };

  const deleteApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/admin/volunteers/applications/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Application deleted successfully!');
      fetchApplications();
      setShowModal(false);
    } catch (error) {
      console.error('Error deleting application:', error);
      toast.error('Failed to delete application. Please try again.');
    }
  };

  const handleFilterStatusChange = (value) => {
    setFilterStatus(value);
    setPagination(p => ({ ...p, page: 1 }));
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPagination(p => ({ ...p, page: 1 }));
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: { bg: '#FFC107', text: '#000' },
      reviewed: { bg: ARCA_COLORS.primary, text: '#FFF' },
      approved: { bg: ARCA_COLORS.secondary, text: '#FFF' },
      rejected: { bg: '#DC3545', text: '#FFF' },
      contacted: { bg: '#17A2B8', text: '#FFF' }
    };
    return statusColors[status] || { bg: '#6C757D', text: '#FFF' };
  };

  if (loading) {
    return <div className="text-center py-4">
      <div className="spinner-border" style={{ color: ARCA_COLORS.primary }} role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-2 text-muted">Loading volunteer applications...</p>
    </div>;
  }

  return (
    <div className="container-fluid">
      {/* Statistics Cards */}
      {statistics && (
        <div className="row g-3 mb-4">
          <div className="col-md-2">
            <div className="card border-0 shadow-sm" style={{ borderLeft: `4px solid ${ARCA_COLORS.primary}` }}>
              <div className="card-body">
                <h6 className="text-muted mb-1">Total</h6>
                <h3 className="mb-0 fw-bold" style={{ color: ARCA_COLORS.primary }}>{statistics.total || 0}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #FFC107' }}>
              <div className="card-body">
                <h6 className="text-muted mb-1">Pending</h6>
                <h3 className="mb-0 fw-bold" style={{ color: '#FFC107' }}>{statistics.pending || 0}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card border-0 shadow-sm" style={{ borderLeft: `4px solid ${ARCA_COLORS.primary}` }}>
              <div className="card-body">
                <h6 className="text-muted mb-1">Reviewed</h6>
                <h3 className="mb-0 fw-bold" style={{ color: ARCA_COLORS.primary }}>{statistics.reviewed || 0}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card border-0 shadow-sm" style={{ borderLeft: `4px solid ${ARCA_COLORS.secondary}` }}>
              <div className="card-body">
                <h6 className="text-muted mb-1">Approved</h6>
                <h3 className="mb-0 fw-bold" style={{ color: ARCA_COLORS.secondary }}>{statistics.approved || 0}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #17A2B8' }}>
              <div className="card-body">
                <h6 className="text-muted mb-1">Contacted</h6>
                <h3 className="mb-0 fw-bold" style={{ color: '#17A2B8' }}>{statistics.contacted || 0}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #DC3545' }}>
              <div className="card-body">
                <h6 className="text-muted mb-1">Rejected</h6>
                <h3 className="mb-0 fw-bold" style={{ color: '#DC3545' }}>{statistics.rejected || 0}</h3>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h2 fw-bold" style={{ color: ARCA_COLORS.secondary }}>Volunteer Applications</h1>
        <div className="d-flex align-items-center gap-3">
          <button
            onClick={() => { fetchApplications(); fetchStatistics(); }}
            className="btn btn-sm text-white"
            style={{ backgroundColor: ARCA_COLORS.primary }}
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <div className="text-sm text-muted">
            Total Applications: <span className="fw-bold" style={{ color: ARCA_COLORS.primary }}>{pagination.total}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 row g-3">
        <div className="col-md-6">
          <input
            type="text"
            placeholder="Search by name, email, or country..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="form-control"
          />
        </div>
        <div className="col-md-6">
          <select
            value={filterStatus}
            onChange={(e) => handleFilterStatusChange(e.target.value)}
            className="form-select"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="contacted">Contacted</option>
          </select>
        </div>
      </div>

      {/* Applications Table */}
      <div className="card shadow-sm border-0">
        <div className="card-header" style={{ backgroundColor: ARCA_COLORS.primary, color: '#FFF' }}>
          <h5 className="mb-0 fw-bold">All Volunteer Applications</h5>
        </div>
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead style={{ backgroundColor: ARCA_COLORS.light }}>
              <tr>
                <th>ID</th>
                <th>Applicant</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Country/City</th>
                <th>Profession</th>
                <th>Availability</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center text-muted py-4">
                    {loading ? 'Loading applications...' : 'No volunteer applications found'}
                  </td>
                </tr>
              ) : (
                applications.map((application) => (
                  <tr key={application.id}>
                    <td className="text-muted">
                      <span className="badge" style={{ backgroundColor: ARCA_COLORS.light, color: ARCA_COLORS.dark }}>#{application.id}</span>
                    </td>
                    <td>
                      <div className="fw-medium" style={{ color: ARCA_COLORS.secondary }}>
                        {application.first_name} {application.last_name}
                      </div>
                    </td>
                    <td className="text-muted" style={{ fontSize: '0.9rem' }}>
                      {application.email}
                    </td>
                    <td className="text-muted" style={{ fontSize: '0.9rem' }}>
                      {application.phone}
                    </td>
                    <td className="text-muted">
                      <div>{application.country}</div>
                      <small className="text-muted">{application.city}</small>
                    </td>
                    <td className="text-muted">
                      {application.profession}
                    </td>
                    <td className="text-muted">
                      <span className="badge bg-secondary">{application.availability}</span>
                    </td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: getStatusBadge(application.status).bg,
                          color: getStatusBadge(application.status).text
                        }}
                      >
                        {application.status}
                      </span>
                    </td>
                    <td className="text-muted" style={{ fontSize: '0.85rem' }}>
                      {new Date(application.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <button
                        onClick={() => viewApplication(application)}
                        className="btn btn-sm me-2 text-white"
                        style={{ backgroundColor: ARCA_COLORS.primary }}
                      >
                        <i className="bi bi-eye me-1"></i>View
                      </button>
                      <button
                        onClick={() => deleteApplication(application.id)}
                        className="btn btn-sm btn-outline-danger"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <nav className="mt-3">
          <ul className="pagination justify-content-center flex-wrap">
            <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>Previous</button>
            </li>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
              <li key={page} className={`page-item ${pagination.page === page ? 'active' : ''}`}>
                <button className="page-link" onClick={() => setPagination(p => ({ ...p, page }))}>{page}</button>
              </li>
            ))}
            <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>Next</button>
            </li>
          </ul>
        </nav>
      )}

      {/* View Application Modal */}
      {showModal && selectedApplication && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: ARCA_COLORS.primary, color: '#FFF' }}>
                <h3 className="modal-title mb-0">Volunteer Application Details</h3>
                <button onClick={() => setShowModal(false)} className="btn-close btn-close-white" type="button" aria-label="Close"></button>
              </div>

              <div className="modal-body">
                <div className="row g-4">
                  {/* Personal Information */}
                  <div className="col-12">
                    <div className="p-3 rounded" style={{ backgroundColor: ARCA_COLORS.light }}>
                      <h4 className="fw-semibold mb-3" style={{ color: ARCA_COLORS.secondary }}>Personal Information</h4>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">First Name</label>
                          <p className="mb-0">{selectedApplication.first_name}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Last Name</label>
                          <p className="mb-0">{selectedApplication.last_name}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Email</label>
                          <p className="mb-0">{selectedApplication.email}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Phone</label>
                          <p className="mb-0">{selectedApplication.phone}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Country</label>
                          <p className="mb-0">{selectedApplication.country}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">City</label>
                          <p className="mb-0">{selectedApplication.city}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Professional Information */}
                  <div className="col-12">
                    <div className="p-3 rounded" style={{ backgroundColor: ARCA_COLORS.light }}>
                      <h4 className="fw-semibold mb-3" style={{ color: ARCA_COLORS.secondary }}>Professional Information</h4>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Profession</label>
                          <p className="mb-0">{selectedApplication.profession}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Availability</label>
                          <p className="mb-0">{selectedApplication.availability}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Interests */}
                  <div className="col-12">
                    <div className="p-3 rounded" style={{ backgroundColor: ARCA_COLORS.light }}>
                      <h4 className="fw-semibold mb-3" style={{ color: ARCA_COLORS.secondary }}>Areas of Interest</h4>
                      <div>
                        {(() => {
                          try {
                            const interests = selectedApplication.interests;
                            if (!interests) return <span className="text-muted">No interests specified</span>;

                            // If already an array
                            if (Array.isArray(interests)) {
                              return interests.map((interest, index) => (
                                <span key={index} className="badge me-2 mb-2 text-white" style={{ backgroundColor: ARCA_COLORS.primary }}>
                                  {interest}
                                </span>
                              ));
                            }

                            // If it's a JSON string
                            if (interests.startsWith('[')) {
                              const parsed = JSON.parse(interests);
                              return parsed.map((interest, index) => (
                                <span key={index} className="badge me-2 mb-2 text-white" style={{ backgroundColor: ARCA_COLORS.primary }}>
                                  {interest}
                                </span>
                              ));
                            }

                            // If it's a plain string, split by comma
                            const interestArray = interests.split(',').map(i => i.trim()).filter(i => i);
                            return interestArray.map((interest, index) => (
                              <span key={index} className="badge me-2 mb-2 text-white" style={{ backgroundColor: ARCA_COLORS.primary }}>
                                {interest}
                              </span>
                            ));
                          } catch (error) {
                            console.error('Error parsing interests:', error);
                            return <span className="text-muted">{selectedApplication.interests}</span>;
                          }
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Experience */}
                  {selectedApplication.experience && (
                    <div className="col-12">
                      <div className="p-3 rounded" style={{ backgroundColor: ARCA_COLORS.light }}>
                        <h4 className="fw-semibold mb-3" style={{ color: ARCA_COLORS.secondary }}>Previous Experience</h4>
                        <p className="mb-0">{selectedApplication.experience}</p>
                      </div>
                    </div>
                  )}

                  {/* Motivation */}
                  <div className="col-12">
                    <div className="p-3 rounded" style={{ backgroundColor: ARCA_COLORS.light }}>
                      <h4 className="fw-semibold mb-3" style={{ color: ARCA_COLORS.secondary }}>Motivation</h4>
                      <p className="mb-0">{selectedApplication.motivation}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <div className="w-100">
                  <h4 className="fw-semibold mb-3" style={{ color: ARCA_COLORS.secondary }}>Update Status</h4>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <button
                      onClick={() => updateStatus(selectedApplication.id, 'pending')}
                      className="btn"
                      style={{ backgroundColor: '#FFC107', color: '#000' }}
                    >
                      Mark as Pending
                    </button>
                    <button
                      onClick={() => updateStatus(selectedApplication.id, 'reviewed')}
                      className="btn text-white"
                      style={{ backgroundColor: ARCA_COLORS.primary }}
                    >
                      Mark as Reviewed
                    </button>
                    <button
                      onClick={() => updateStatus(selectedApplication.id, 'approved')}
                      className="btn text-white"
                      style={{ backgroundColor: ARCA_COLORS.secondary }}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => updateStatus(selectedApplication.id, 'rejected')}
                      className="btn"
                      style={{ backgroundColor: '#DC3545', color: '#FFF' }}
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => updateStatus(selectedApplication.id, 'contacted')}
                      className="btn"
                      style={{ backgroundColor: '#17A2B8', color: '#FFF' }}
                    >
                      Mark as Contacted
                    </button>
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      onClick={() => setShowModal(false)}
                      className="btn btn-secondary"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showModal && selectedApplication && (
        <div className="modal-backdrop show"></div>
      )}
    </div>
  );
};

export default VolunteerApplications;
